<template>
  <div class="login-wrapper d-flex flex-wrap overflow-hidden">
    <div class="login-bg"></div>
    <div class="login-form-wrap">
      <div class="login-form">
        <router-link to="/" class="logo">
          <img
            src="../../assets/img/logo.jpg"
            alt="Logo"
            class="d-block mx-auto logo-custom-w-100"
          />
        </router-link>
        <form @submit.prevent="handleLogin">
          <!-- Username -->
          <div class="form-group mb-15">
            <label class="d-block fs-14 text-black mb-2">Username</label>
            <input
              type="text"
              class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-title"
              placeholder="Enter Username"
              v-model="username"
              required
            />
          </div>

          <!-- Password -->
          <div class="form-group mb-20 position-relative">
            <label class="d-block fs-14 text-black mb-2">Password</label>
            <input
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Enter Password"
              class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black"
              v-model="password"
              required
            />
            <span
              id="toggler"
              class="position-absolute"
              @click="togglePasswordVisibility"
            >
              <img
                v-if="!passwordVisible"
                src="../../assets/img/icons/eye-close.svg"
                alt="Show Password"
              />
              <img
                v-else
                src="../../assets/img/icons/eye.svg"
                alt="Hide Password"
              />
            </span>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="text-danger mb-3">
            {{ errorMessage }}
          </div>

          <!-- Demo Credentials -->
          <div class="alert alert-info mb-3">
            <small>
              <strong>Demo Credentials:</strong><br>
              Username: <code>Admin</code><br>
              Password: <code>password123</code>
            </small>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="btn style-one w-100 d-block"
            :disabled="loading"
          >
            {{ loading ? "Logging in..." : "Login" }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { authService } from "../../services/auth";
import type { AxiosError } from "axios";

export default defineComponent({
  name: "LoginPage",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const username = ref("");
    const password = ref("");
    const loading = ref(false);
    const errorMessage = ref("");
    const passwordVisible = ref(false);

    const togglePasswordVisibility = () => {
      passwordVisible.value = !passwordVisible.value;
    };

    const handleLogin = async () => {
      loading.value = true;
      errorMessage.value = "";

      try {
        console.log('Attempting login with:', { username: username.value, password: password.value });
        await authService.login(username.value, password.value);
        
        // Redirect to the original intended path or dashboard
        const redirect = route.query.redirect as string;
        console.log('Login successful, redirecting to:', redirect || '/');
        router.push(redirect || "/");
      } catch (err: unknown) {
        const e = err as AxiosError<{ message?: string }>;
        console.error('Login error in component:', e);
        if (e.code === 'ERR_NETWORK' || e.message?.includes('connect')) {
          errorMessage.value = "Cannot connect to the server. Please make sure the backend is running on port 8000.";
        } else if (e.code === 'ECONNREFUSED') {
          errorMessage.value = "Connection refused. The backend server may not be running.";
        } else if (e.code === 'ETIMEDOUT') {
          errorMessage.value = "Connection timeout. The server is taking too long to respond.";
        } else if (e.response?.status === 401) {
          errorMessage.value = "Invalid username or password. Please check your credentials.";
        } else if (e.response?.status === 422) {
          errorMessage.value = "Please fill in all required fields correctly.";
        } else if (e.response?.data?.message) {
          errorMessage.value = e.response.data.message as string;
        } else if (e.message) {
          errorMessage.value = e.message;
        } else {
          errorMessage.value = "Login failed. Please try again.";
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      username,
      password,
      loading,
      errorMessage,
      passwordVisible,
      togglePasswordVisibility,
      handleLogin,
    };
  },
});
</script>

<style lang="scss" scoped>
.login-wrapper {
  display: flex;
  height: 100vh;

  .login-bg {
    background-image: url(../../assets/img/login-bg.webp);
    background-size: cover;
    background-position: center;
    width: 50%;
  }

  .login-form-wrap {
    width: 50%;
    background-color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 50px;

    .login-form {
      width: 100%;
      border: 1px solid #ccc;
      padding: 30px;
      .logo {
        display: block;
        margin-bottom: 20px;
        text-align: center;
      }
    }
  }

  #toggler {
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
  }
}

@media (max-width: 991px) {
  .login-wrapper {
    flex-direction: column;
    .login-bg {
      display: none;
    }
    .login-form-wrap {
      width: 100%;
      padding: 0 12px;
    }
  }
}
</style>
